import { Typography } from '@mui/material';
import React, { useContext } from 'react';
import { OverwriteContext } from '../providers';
import { Loading } from './Loading';

function loadWebpackModule(
  scope: string,
  module: string,
): () => Promise<{ default: () => JSX.Element }> {
  return async () => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    await __webpack_init_sharing__('default');
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const container = window[scope];
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    await container.init(__webpack_share_scopes__.default);
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const factory = await window[scope].get(`./${module}`);
    return factory();
  };
}

const cache: Record<
  string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  React.ComponentType<any>
> = {};

function loadComponent<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  T extends React.ComponentType<any> = React.LazyExoticComponent<
    React.ComponentType<unknown>
  >,
>(scope: string, module: string): T {
  const key = `${scope}-${module}`;
  if (!cache[key]) {
    cache[key] = React.lazy(loadWebpackModule(scope, module)) as unknown as T;
  }
  return cache[key] as unknown as T;
}

const urlCache = new Set();
const loadDynamicScript = (url: string) => {
  return new Promise<void>((resolve, reject) => {
    if (!url) {
      reject(new Error('No url provided'));
      return;
    }

    if (urlCache.has(url)) {
      resolve();
      return;
    }

    const element = document.createElement('script');

    element.src = url;
    element.type = 'text/javascript';
    element.async = true;

    element.onload = () => {
      resolve();
    };

    element.onerror = (e) => {
      urlCache.delete(url);
      reject(new Error(`Error loading script ${url} - ${e}`));
    };
    urlCache.add(url);
    document.head.appendChild(element);
  });
};

const useDynamicScripts = (urls: string[], urlsstring: string) => {
  const [scriptReadySet, setScriptReadySet] = React.useState(new Set());
  const [errorLoading, setErrorLoading] = React.useState(false);

  const loadingScripts = React.useRef(false);

  React.useEffect(() => {
    const loadAll = async () => {
      loadingScripts.current = true;
      try {
        await Promise.all(urls.map(loadDynamicScript));
        setErrorLoading(false);
      } catch (e) {
        setErrorLoading(true);
      } finally {
        loadingScripts.current = false;
        setScriptReadySet((prev) => new Set(prev.add(urlsstring)));
      }
    };

    if (!loadingScripts.current && !scriptReadySet.has(urlsstring)) {
      void loadAll();
    }
  }, [urlsstring, scriptReadySet, urls]);

  return {
    errorLoading,
    scriptReadySet,
  };
};

export type FederatedComponentProps = {
  url: string;
  scope: string;
  module: string;
};

export const useFederatedComponents = <
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  DependencyComponentType extends React.ComponentType<any> = React.LazyExoticComponent<
    React.ComponentType<unknown>
  >,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  MainComponentType extends React.ComponentType<any> = React.LazyExoticComponent<
    React.ComponentType<{
      dependencies: {
        [key: string]: DependencyComponentType;
      };
    }>
  >,
>(
  mainComponent: FederatedComponentProps,
  dependencyComponents: FederatedComponentProps[] = [],
) => {
  const urls = React.useMemo(
    () => [
      mainComponent.url,
      ...dependencyComponents.map((child) => child.url),
    ],
    [mainComponent, dependencyComponents],
  );
  const urlsstring = urls.join('');
  const [Component, setComponent] = React.useState<MainComponentType | null>(
    null,
  );
  const [DependencyComponents, setDependencyComponents] = React.useState<{
    [key: string]: DependencyComponentType;
  }>({});

  const { scriptReadySet, errorLoading } = useDynamicScripts(urls, urlsstring);

  const scriptsReady = scriptReadySet.has(urlsstring);

  const container = window[mainComponent.scope as never];

  React.useEffect(() => {
    if (scriptsReady && container) {
      const MainComponent = loadComponent<MainComponentType>(
        mainComponent.scope,
        mainComponent.module,
      );
      setComponent(MainComponent);
      dependencyComponents.forEach((depC) => {
        const DependencyComponent = loadComponent<DependencyComponentType>(
          depC.scope,
          depC.module,
        );
        const moduleRef = `${depC.scope}/${depC.module}`; //e.g. 'Loads/SomeDialog'
        setDependencyComponents((prev) => {
          prev[moduleRef] = DependencyComponent;
          return prev;
        });
      });
    }
  }, [
    scriptsReady,
    container,
    mainComponent.module,
    mainComponent.scope,
    dependencyComponents,
  ]);

  return {
    errorLoadingFederatedComponent: errorLoading,
    FederatedComponent: Component,
    DependencyComponents: DependencyComponents,
  };
};

export type DynamicRemoteComponentProps = {
  exposedFrom: string;
  dependencies?: string[];
  dynamicRemotesConfig: DynamicRemotes;
};

export type DynamicRemotes = {
  stage: Record<string, string>;
  prod: Record<string, string>;
};

export function getFederatedComponentProps(
  from: string,
  dynamicRemotes: DynamicRemotes,
): FederatedComponentProps {
  const [scope, module] = from.split('/');
  const env = process.env.APP_ENV as 'stage' | 'prod';
  const url = dynamicRemotes[env][scope];
  if (!url) {
    throw new Error(
      `dynamic remotes config doesn't contain env '${env}' with scope '${scope}' `,
    );
  }
  return { url, scope, module };
}

export function includeRemoteOverwrites(
  overwriteConfig: Record<string, string>,
  remotesConfig: DynamicRemotes,
) {
  const result = {
    stage: { ...remotesConfig.stage },
    prod: { ...remotesConfig.prod },
  };
  for (const key in overwriteConfig) {
    if (remotesConfig.stage[key]) {
      result.stage[key] = overwriteConfig[key];
    }
  }
  return result;
}

export function DynamicRemoteComponent(
  props: DynamicRemoteComponentProps,
): JSX.Element {
  const overwriteContext = useContext(OverwriteContext);
  const dynamicRemotesConfig = includeRemoteOverwrites(
    overwriteContext,
    props.dynamicRemotesConfig,
  );
  const {
    FederatedComponent,
    DependencyComponents,
    errorLoadingFederatedComponent,
  } = useFederatedComponents(
    getFederatedComponentProps(props.exposedFrom, dynamicRemotesConfig),
    props.dependencies?.map((dep) =>
      getFederatedComponentProps(dep, dynamicRemotesConfig),
    ),
  );

  return (
    <React.Suspense fallback={<Loading />}>
      {errorLoadingFederatedComponent ? (
        <div>
          <Typography variant="h2">
            Error loading module from {props.exposedFrom}.
          </Typography>
        </div>
      ) : (
        FederatedComponent && (
          <FederatedComponent dependencies={DependencyComponents} />
        )
      )}
    </React.Suspense>
  );
}
